import { useState, useEffect, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Loader2, Check, X, Puzzle, Key, ExternalLink, Shield, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { useCapabilityCreate } from '@/hooks/use-entity-mutations';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { useCapabilityDetail } from '@/hooks/use-entity-detail';
import { getCapabilityColors } from '@/lib/capability-colors';
import { cn } from '@/lib/utils';
import { logger } from '@/lib/telemetry';
import { oauthStrategyFromTypeConfig, startOAuthAuthorizationCode } from '@/lib/oauth-connect';
import { capabilityDetailPath } from '@/types/capability-types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { CreatedCapability } from '../CreateCapabilityDrawer';
import type { Capability } from '@/types/capability-types';

const WIZARD_STEPS = [
    { id: 'select_integration', title: 'Select Integration', description: 'Choose the integration to connect to' },
    { id: 'auth', title: 'Authentication', description: 'Configure credentials for this connection' },
    { id: 'confirm', title: 'Review & Create', description: 'Review and create the connection' },
] as const;

const TOTAL_STEPS = WIZARD_STEPS.length;

const AUTH_TYPE_LABELS: Record<string, string> = {
    oauth2_authorization_code: 'OAuth2 (Authorization Code)',
    oauth2_client_credentials: 'OAuth2 (Client Credentials)',
    api_key: 'API Key',
    basic_auth: 'Basic Authentication',
    bearer_token: 'Bearer Token',
    hmac: 'HMAC Signing',
    custom_header: 'Custom Header',
};

const AUTH_TYPE_ICONS: Record<string, typeof KeyRound> = {
    oauth2_authorization_code: ExternalLink,
    oauth2_client_credentials: Shield,
    api_key: KeyRound,
    basic_auth: KeyRound,
    bearer_token: KeyRound,
    hmac: Shield,
    custom_header: KeyRound,
};

interface ConnectionFormData {
    integrationRef: string;
    integrationName: string;
    connectionName: string;
    authType: string;
    credentials: Record<string, string>;
    metadata: Record<string, string>;
}

function createDefaultConnectionForm(): ConnectionFormData {
    return {
        integrationRef: '',
        integrationName: '',
        connectionName: '',
        authType: '',
        credentials: {},
        metadata: {},
    };
}

export interface ConnectionCreateWizardProps {
    appKey: string;
    onClose: () => void;
    onSuccess?: (entity: CreatedCapability) => void;
    preselectedIntegration?: string;
    preselectedAuthTypes?: string[];
}

export function ConnectionCreateWizard({
    appKey,
    onClose,
    onSuccess,
    preselectedIntegration,
    preselectedAuthTypes,
}: ConnectionCreateWizardProps) {
    const createCapability = useCapabilityCreate(appKey);
    const colors = getCapabilityColors('connection');
    const TypeIcon = colors.icon;

    const [currentStep, setCurrentStep] = useState(preselectedIntegration ? 1 : 0);
    const [formData, setFormData] = useState<ConnectionFormData>(() => {
        const defaults = createDefaultConnectionForm();
        if (preselectedIntegration) {
            defaults.integrationRef = preselectedIntegration;
            defaults.integrationName = preselectedIntegration.split('.').pop()?.replace(/-/g, ' ') ?? preselectedIntegration;
        }
        return defaults;
    });
    const [errors, setErrors] = useState<Record<string, string>>({});

    const { data: appIntegrationsData, isLoading: appIntegrationsLoading } = useCapabilityList(appKey, { type: 'integration' });
    const { data: globalIntegrationsData, isLoading: globalIntegrationsLoading } = useCapabilityList(undefined, { type: 'integration' });
    const integrationsLoading = appIntegrationsLoading || globalIntegrationsLoading;

    const integrations = useMemo(() => {
        const appItems: Capability[] = (appIntegrationsData as { data?: Capability[] })?.data
            ?? (Array.isArray(appIntegrationsData) ? appIntegrationsData : []);
        const globalItems: Capability[] = (globalIntegrationsData as { data?: Capability[] })?.data
            ?? (Array.isArray(globalIntegrationsData) ? globalIntegrationsData : []);

        const seen = new Set<string>();
        const merged: Capability[] = [];
        for (const item of [...appItems, ...globalItems]) {
            if (seen.has(item.name)) continue;
            seen.add(item.name);

            const tc = (item.config ?? item.ext ?? {}) as Record<string, unknown>;
            const auth = tc.auth as { supported_types?: string[] } | undefined;
            if (auth && (!auth.supported_types || auth.supported_types.length === 0)) {
                continue;
            }

            merged.push(item);
        }
        return merged;
    }, [appIntegrationsData, globalIntegrationsData]);

    const selectedIntegrationAppKey = useMemo(() => {
        if (!formData.integrationRef) return '';
        const found = integrations.find((i) => i.name === formData.integrationRef);
        return found?.app_key ?? appKey;
    }, [formData.integrationRef, integrations, appKey]);

    const { data: selectedIntegrationDetail } = useCapabilityDetail(
        selectedIntegrationAppKey,
        formData.integrationRef,
    );

    const supportedAuthTypes = useMemo(() => {
        if (preselectedAuthTypes?.length) return preselectedAuthTypes;
        if (!selectedIntegrationDetail) return [];
        const detail = selectedIntegrationDetail as Record<string, unknown>;
        const wr = detail.working_revision as { type_config?: Record<string, unknown> } | undefined;
        const tc = (wr?.type_config ?? detail.config ?? detail.ext ?? {}) as Record<string, unknown>;
        const auth = tc.auth as { supported_types?: string[] } | undefined;
        return auth?.supported_types ?? [];
    }, [selectedIntegrationDetail, preselectedAuthTypes]);

    const oauthStrategy = useMemo(() => {
        const detail = selectedIntegrationDetail as Record<string, unknown> | undefined;
        const wr = detail?.working_revision as { type_config?: Record<string, unknown> } | undefined;
        const tc = (wr?.type_config ?? detail?.config ?? detail?.ext ?? {}) as Record<string, unknown>;
        return oauthStrategyFromTypeConfig(tc);
    }, [selectedIntegrationDetail]);

    useEffect(() => {
        setErrors({});
    }, [currentStep]);

    useEffect(() => {
        if (currentStep === 1 && supportedAuthTypes.length === 1 && !formData.authType) {
            setFormData((prev) => ({ ...prev, authType: supportedAuthTypes[0], credentials: {} }));
        }
    }, [currentStep, supportedAuthTypes, formData.authType]);

    const validateStep = (step: number): boolean => {
        const errs: Record<string, string> = {};

        if (step === 0) {
            if (!formData.integrationRef) errs.integration = 'Please select an integration';
        }
        if (step === 1) {
            if (!formData.authType) errs.authType = 'Please select an authentication method';
            if (formData.authType === 'api_key' || formData.authType === 'bearer_token') {
                if (!formData.credentials.api_key?.trim()) errs.api_key = 'API key / token is required';
            }
            if (formData.authType === 'basic_auth') {
                if (!formData.credentials.username?.trim()) errs.username = 'Username is required';
                if (!formData.credentials.password?.trim()) errs.password = 'Password is required';
            }
            if (formData.authType === 'oauth2_client_credentials') {
                if (!formData.credentials.client_id?.trim()) errs.client_id = 'Client ID is required';
                if (!formData.credentials.client_secret?.trim()) errs.client_secret = 'Client Secret is required';
            }
            if (formData.authType === 'oauth2_authorization_code') {
                if (!oauthStrategy.authUrl) {
                    errs.authType = 'This integration has no authorization URL. Discover the MCP server or set it on the Authorization tab.';
                }
                if (!formData.credentials.client_id?.trim() && !oauthStrategy.registrationEndpoint) {
                    errs.client_id = 'Client ID is required (or the integration must support dynamic client registration)';
                }
            }
        }
        if (step === 2) {
            if (!formData.connectionName.trim()) errs.connectionName = 'Connection name is required';
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const goNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));
        }
    };

    const goBack = () => {
        setCurrentStep((prev) => Math.max(prev - 1, preselectedIntegration ? 1 : 0));
    };

    const handleSubmit = async () => {
        if (!validateStep(currentStep)) return;

        try {
            const slug = formData.connectionName
                .toLowerCase()
                .replace(/[^a-z0-9\s-]/g, '')
                .replace(/\s+/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');

            const isAuthCode = formData.authType === 'oauth2_authorization_code';
            const typeConfig = {
                integration_ref: formData.integrationRef,
                auth_type: formData.authType,
                status: isAuthCode ? 'pending' : 'active',
                owner: {
                    tenant_id: appKey,
                },
                credentials: formData.credentials,
                metadata: {
                    ...formData.metadata,
                    connected_at: new Date().toISOString(),
                },
            };

            const payload = {
                name: `connection.${slug}`,
                display_name: formData.connectionName,
                type: 'connection',
                description: `Connection to ${formData.integrationName} via ${AUTH_TYPE_LABELS[formData.authType] ?? formData.authType}`,
                tags: ['connection', formData.authType],
                type_config: typeConfig,
            };

            logger.info('ConnectionCreateWizard:handleSubmit', { name: payload.name });
            const result = await createCapability.mutateAsync(payload);
            const createdName = result && typeof result === 'object' && 'name' in result
                ? String((result as CreatedCapability).name)
                : payload.name;

            if (isAuthCode) {
                toast.success(`Connection "${formData.connectionName}" created. Redirecting to sign in…`);
                await startOAuthAuthorizationCode({
                    connection_name: createdName,
                    app_key: appKey,
                    integration_ref: formData.integrationRef,
                    auth_url: oauthStrategy.authUrl,
                    token_url: oauthStrategy.tokenUrl,
                    client_id: formData.credentials.client_id,
                    client_secret: formData.credentials.client_secret,
                    scopes: oauthStrategy.scopes,
                    pkce: oauthStrategy.pkce,
                    registration_endpoint: oauthStrategy.registrationEndpoint,
                    resource: oauthStrategy.resource,
                    return_to: capabilityDetailPath(appKey, 'connection', createdName),
                });
                return;
            }

            toast.success(`Connection "${formData.connectionName}" created`);
            if (onSuccess && result && typeof result === 'object' && 'name' in result) {
                onSuccess(result as CreatedCapability);
            }
            onClose();
        } catch (err: unknown) {
            logger.error('ConnectionCreateWizard:handleSubmit', { error: err });
            const msg = err instanceof Error ? err.message : 'Failed to create connection';
            toast.error(msg);
        }
    };

    const isLastStep = currentStep === TOTAL_STEPS - 1;
    const canGoNext = currentStep === 0
        ? !!formData.integrationRef
        : currentStep === 1
            ? !!formData.authType
            : true;
    const stepTitle = WIZARD_STEPS[currentStep]?.title ?? '';

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6">
            {/* Header */}
            <div className="relative shrink-0 border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={cn('flex h-10 w-10 items-center justify-center rounded-full', colors.bgLight)}>
                            <TypeIcon className={cn('h-5 w-5', colors.text)} />
                        </div>
                        <div>
                            <h2 className="text-xl font-semibold text-slate-900">
                                Add New Connection
                            </h2>
                            <p className="text-sm text-slate-500">
                                Step {currentStep + 1} of {TOTAL_STEPS}: {stepTitle}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Step Indicators */}
                <div className="mt-3 flex items-center gap-2 px-16 pt-4">
                    {WIZARD_STEPS.map((step, index) => (
                        <div key={step.id} className="flex items-center">
                            <div
                                className={cn(
                                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                                    index < currentStep
                                        ? 'bg-emerald-500 text-white'
                                        : index === currentStep
                                            ? `${colors.bgLight} ${colors.text} ring-2 ring-emerald-500`
                                            : 'bg-slate-100 text-slate-400',
                                )}
                            >
                                {index < currentStep ? <Check size={16} /> : index + 1}
                            </div>
                            {index < WIZARD_STEPS.length - 1 && (
                                <div
                                    className={cn(
                                        'mx-2 h-0.5 w-12 transition-colors',
                                        index < currentStep ? 'bg-emerald-500' : 'bg-slate-200',
                                    )}
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto bg-white px-6 py-4">
                <AnimatePresence mode="wait">
                    <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                    >
                        {currentStep === 0 && (
                            <StepSelectIntegration
                                integrations={integrations}
                                isLoading={integrationsLoading}
                                selectedRef={formData.integrationRef}
                                onSelect={(ref, name) => setFormData({
                                    ...formData,
                                    integrationRef: ref,
                                    integrationName: name,
                                    authType: '',
                                    credentials: {},
                                })}
                                errors={errors}
                                appKey={appKey}
                            />
                        )}
                        {currentStep === 1 && (
                            <StepConfigureAuth
                                supportedAuthTypes={supportedAuthTypes}
                                selectedAuthType={formData.authType}
                                credentials={formData.credentials}
                                onAuthTypeChange={(authType) => setFormData({ ...formData, authType, credentials: {} })}
                                onCredentialsChange={(credentials) => setFormData({ ...formData, credentials })}
                                errors={errors}
                                integrationName={formData.integrationName}
                                oauthStrategy={oauthStrategy}
                            />
                        )}
                        {currentStep === 2 && (
                            <StepConfirm
                                formData={formData}
                                onChange={(name) => setFormData({ ...formData, connectionName: name })}
                                errors={errors}
                            />
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                <div>
                    {currentStep > (preselectedIntegration ? 1 : 0) && (
                        <button
                            type="button"
                            onClick={goBack}
                            disabled={createCapability.isPending}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                            Back
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={createCapability.isPending}
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    {isLastStep ? (
                        <button
                            type="button"
                            onClick={() => void handleSubmit()}
                            disabled={createCapability.isPending || !formData.connectionName.trim()}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {createCapability.isPending ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                <>
                                    Create Connection
                                    <Check size={16} />
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={!canGoNext}
                            className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Step 1: Select Integration ──────────────────────────────────────

function StepSelectIntegration({
    integrations,
    isLoading,
    selectedRef,
    onSelect,
    errors,
    appKey,
}: {
    integrations: Capability[];
    isLoading: boolean;
    selectedRef: string;
    onSelect: (ref: string, name: string) => void;
    errors: Record<string, string>;
    appKey: string;
}) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                <span className="ml-2 text-sm text-slate-500">Loading integrations...</span>
            </div>
        );
    }

    if (integrations.length === 0) {
        return (
            <div className="rounded-lg border-2 border-dashed border-slate-200 p-8 text-center">
                <Puzzle className="mx-auto h-10 w-10 text-slate-300" />
                <p className="mt-3 text-sm font-medium text-slate-600">No integrations available</p>
                <p className="mt-1 text-xs text-slate-400">
                    Create an integration first, then come back to set up a connection.
                </p>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-2xl space-y-4">
            <div>
                <h3 className="text-base font-semibold text-slate-900">Select an Integration</h3>
                <p className="mt-1 text-sm text-slate-500">
                    Choose which integration blueprint this connection will authenticate against.
                    Both app-local and public integrations are shown.
                </p>
            </div>
            {errors.integration && (
                <p className="text-xs text-red-500">{errors.integration}</p>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {integrations.map((integration) => {
                    const tc = (integration.config ?? integration.ext ?? {}) as Record<string, unknown>;
                    const app = tc.app as { name?: string; icon_url?: string; category?: string } | undefined;
                    const auth = tc.auth as { supported_types?: string[] } | undefined;
                    const displayName = app?.name ?? integration.display_name ?? integration.name;
                    const isSelected = selectedRef === integration.name;
                    const isLocal = integration.app_key === appKey;

                    return (
                        <button
                            key={integration.name}
                            type="button"
                            onClick={() => onSelect(integration.name, displayName)}
                            className={cn(
                                'flex items-start gap-3 rounded-lg border p-4 text-left transition-all',
                                isSelected
                                    ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/20'
                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
                            )}
                        >
                            <div className={cn(
                                'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                                isSelected ? 'bg-emerald-100' : 'bg-violet-50',
                            )}>
                                {app?.icon_url ? (
                                    <img src={app.icon_url} alt="" className="h-6 w-6 rounded" />
                                ) : (
                                    <Puzzle className={cn('h-5 w-5', isSelected ? 'text-emerald-600' : 'text-violet-500')} />
                                )}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <p className={cn(
                                        'text-sm font-medium truncate',
                                        isSelected ? 'text-emerald-900' : 'text-slate-900',
                                    )}>
                                        {displayName}
                                    </p>
                                    <Badge variant="outline" className={cn(
                                        'text-[9px] px-1 py-0 shrink-0',
                                        isLocal ? 'border-blue-200 text-blue-600' : 'border-slate-200 text-slate-500',
                                    )}>
                                        {isLocal ? 'Local' : 'Public'}
                                    </Badge>
                                </div>
                                {integration.description && (
                                    <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">
                                        {integration.description}
                                    </p>
                                )}
                                {auth?.supported_types && auth.supported_types.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {auth.supported_types.map((t) => (
                                            <Badge key={t} variant="outline" className="text-[10px] px-1.5 py-0">
                                                {AUTH_TYPE_LABELS[t] ?? t}
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {isSelected && (
                                <Check className="h-5 w-5 shrink-0 text-emerald-600" />
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ── Step 2: Configure Auth ──────────────────────────────────────────

function StepConfigureAuth({
    supportedAuthTypes,
    selectedAuthType,
    credentials,
    onAuthTypeChange,
    onCredentialsChange,
    errors,
    integrationName,
    oauthStrategy,
}: {
    supportedAuthTypes: string[];
    selectedAuthType: string;
    credentials: Record<string, string>;
    onAuthTypeChange: (type: string) => void;
    onCredentialsChange: (creds: Record<string, string>) => void;
    errors: Record<string, string>;
    integrationName: string;
    oauthStrategy: ReturnType<typeof oauthStrategyFromTypeConfig>;
}) {
    const inputClassName =
        'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
    const errorInputClassName =
        'w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500';

    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <div>
                <h3 className="text-base font-semibold text-slate-900">
                    Authentication for {integrationName}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                    Choose an authentication method and provide your credentials.
                </p>
            </div>

            {/* Auth type selection */}
            {!selectedAuthType && (
                <div className="space-y-3">
                    {errors.authType && (
                        <p className="text-xs text-red-500">{errors.authType}</p>
                    )}
                    {supportedAuthTypes.length > 0 ? (
                        supportedAuthTypes.map((authType) => {
                            const Icon = AUTH_TYPE_ICONS[authType] ?? KeyRound;
                            return (
                                <button
                                    key={authType}
                                    type="button"
                                    onClick={() => onAuthTypeChange(authType)}
                                    className="w-full flex items-center gap-3 rounded-lg border border-slate-200 p-4 text-left transition-all hover:border-emerald-300 hover:bg-emerald-50/50"
                                >
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100">
                                        <Icon className="h-5 w-5 text-slate-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-slate-900">
                                            {AUTH_TYPE_LABELS[authType] ?? authType}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                            {authType.startsWith('oauth2')
                                                ? 'Authorize via provider redirect'
                                                : 'Enter credentials directly'}
                                        </p>
                                    </div>
                                </button>
                            );
                        })
                    ) : (
                        <div className="rounded-lg border-2 border-dashed border-slate-200 p-6 text-center">
                            <KeyRound className="mx-auto h-8 w-8 text-slate-300" />
                            <p className="mt-2 text-sm font-medium text-slate-600">
                                No auth types configured
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                                The selected integration does not define supported authentication methods.
                                Please configure auth types on the integration first.
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Credential forms based on selected auth type */}
            {selectedAuthType && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            <Key size={10} className="mr-1" />
                            {AUTH_TYPE_LABELS[selectedAuthType] ?? selectedAuthType}
                        </Badge>
                        <button
                            type="button"
                            onClick={() => onAuthTypeChange('')}
                            className="text-xs text-slate-400 underline hover:text-slate-600"
                        >
                            Change
                        </button>
                    </div>

                    {(selectedAuthType === 'api_key' || selectedAuthType === 'bearer_token') && (
                        <div className="space-y-2">
                            <Label>
                                {selectedAuthType === 'bearer_token' ? 'Bearer Token' : 'API Key'}{' '}
                                <span className="text-rose-500">*</span>
                            </Label>
                            <Input
                                type="password"
                                placeholder={`Enter your ${selectedAuthType === 'bearer_token' ? 'token' : 'API key'}...`}
                                value={credentials.api_key ?? ''}
                                onChange={(e) => onCredentialsChange({ ...credentials, api_key: e.target.value })}
                                className={errors.api_key ? errorInputClassName : inputClassName}
                            />
                            {errors.api_key && <p className="text-xs text-red-500">{errors.api_key}</p>}
                        </div>
                    )}

                    {selectedAuthType === 'basic_auth' && (
                        <>
                            <div className="space-y-2">
                                <Label>Username <span className="text-rose-500">*</span></Label>
                                <Input
                                    placeholder="Enter username..."
                                    value={credentials.username ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, username: e.target.value })}
                                    className={errors.username ? errorInputClassName : inputClassName}
                                />
                                {errors.username && <p className="text-xs text-red-500">{errors.username}</p>}
                            </div>
                            <div className="space-y-2">
                                <Label>Password <span className="text-rose-500">*</span></Label>
                                <Input
                                    type="password"
                                    placeholder="Enter password..."
                                    value={credentials.password ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, password: e.target.value })}
                                    className={errors.password ? errorInputClassName : inputClassName}
                                />
                                {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                            </div>
                        </>
                    )}

                    {selectedAuthType === 'oauth2_authorization_code' && (
                        <div className="space-y-4">
                            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center">
                                <ExternalLink className="mx-auto h-8 w-8 text-blue-500" />
                                <p className="mt-2 text-sm font-medium text-blue-900">OAuth2 Authorization Code</p>
                                <p className="mt-1 text-xs text-blue-600">
                                    Creating this connection will send you to the provider login page to authorize access.
                                </p>
                            </div>
                            <div className="space-y-2">
                                <Label>
                                    Client ID
                                    {!oauthStrategy.registrationEndpoint && <span className="text-rose-500"> *</span>}
                                </Label>
                                <Input
                                    placeholder={oauthStrategy.registrationEndpoint
                                        ? 'Optional — will register automatically if empty'
                                        : 'OAuth client ID'}
                                    value={credentials.client_id ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, client_id: e.target.value })}
                                    className={errors.client_id ? errorInputClassName : inputClassName}
                                />
                                {errors.client_id && <p className="text-xs text-red-500">{errors.client_id}</p>}
                            </div>
                            {errors.authType && <p className="text-xs text-red-500">{errors.authType}</p>}
                        </div>
                    )}

                    {selectedAuthType === 'oauth2_client_credentials' && (
                        <>
                            <div className="space-y-2">
                                <Label>Client ID <span className="text-rose-500">*</span></Label>
                                <Input
                                    placeholder="Enter client ID..."
                                    value={credentials.client_id ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, client_id: e.target.value })}
                                    className={errors.client_id ? errorInputClassName : inputClassName}
                                />
                                {errors.client_id && <p className="text-xs text-red-500">{errors.client_id}</p>}
                            </div>
                            <div className="space-y-2">
                                <Label>Client Secret <span className="text-rose-500">*</span></Label>
                                <Input
                                    type="password"
                                    placeholder="Enter client secret..."
                                    value={credentials.client_secret ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, client_secret: e.target.value })}
                                    className={errors.client_secret ? errorInputClassName : inputClassName}
                                />
                                {errors.client_secret && <p className="text-xs text-red-500">{errors.client_secret}</p>}
                            </div>
                            <div className="space-y-2">
                                <Label>Scopes</Label>
                                <Input
                                    placeholder="e.g. read,write (comma-separated)"
                                    value={credentials.scopes ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, scopes: e.target.value })}
                                    className={inputClassName}
                                />
                            </div>
                        </>
                    )}

                    {selectedAuthType === 'hmac' && (
                        <div className="space-y-2">
                            <Label>Signing Secret <span className="text-rose-500">*</span></Label>
                            <Input
                                type="password"
                                placeholder="Enter signing secret..."
                                value={credentials.signing_secret ?? ''}
                                onChange={(e) => onCredentialsChange({ ...credentials, signing_secret: e.target.value })}
                                className={inputClassName}
                            />
                        </div>
                    )}

                    {selectedAuthType === 'custom_header' && (
                        <>
                            <div className="space-y-2">
                                <Label>Header Name <span className="text-rose-500">*</span></Label>
                                <Input
                                    placeholder="e.g. X-Custom-Auth"
                                    value={credentials.header_name ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, header_name: e.target.value })}
                                    className={inputClassName}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Header Value <span className="text-rose-500">*</span></Label>
                                <Input
                                    type="password"
                                    placeholder="Enter header value..."
                                    value={credentials.header_value ?? ''}
                                    onChange={(e) => onCredentialsChange({ ...credentials, header_value: e.target.value })}
                                    className={inputClassName}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ── Step 3: Confirm ─────────────────────────────────────────────────

function StepConfirm({
    formData,
    onChange,
    errors,
}: {
    formData: ConnectionFormData;
    onChange: (name: string) => void;
    errors: Record<string, string>;
}) {
    const inputClassName =
        'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
    const errorInputClassName =
        'w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500';

    return (
        <div className="mx-auto max-w-2xl space-y-6">
            <div>
                <h3 className="text-base font-semibold text-slate-900">Review & Create</h3>
                <p className="mt-1 text-sm text-slate-500">
                    Name your connection and confirm the details below.
                </p>
            </div>

            <div className="space-y-2">
                <Label>
                    Connection Name <span className="text-rose-500">*</span>
                </Label>
                <Input
                    placeholder={`e.g. My ${formData.integrationName} Connection`}
                    value={formData.connectionName}
                    onChange={(e) => onChange(e.target.value)}
                    className={errors.connectionName ? errorInputClassName : inputClassName}
                    autoFocus
                />
                {errors.connectionName && (
                    <p className="text-xs text-red-500">{errors.connectionName}</p>
                )}
                <p className="text-xs text-slate-400">
                    A human-readable name to identify this connection.
                </p>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
                <h4 className="text-sm font-medium text-slate-700">Summary</h4>
                <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                    <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Integration</dt>
                        <dd className="mt-0.5 flex items-center gap-1.5">
                            <Puzzle size={14} className="text-violet-500" />
                            <span className="text-slate-900">{formData.integrationName}</span>
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Auth Method</dt>
                        <dd className="mt-0.5 flex items-center gap-1.5">
                            <Key size={14} className="text-blue-500" />
                            <span className="text-slate-900">
                                {AUTH_TYPE_LABELS[formData.authType] ?? formData.authType}
                            </span>
                        </dd>
                    </div>
                    <div className="sm:col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Integration Ref</dt>
                        <dd className="mt-0.5">
                            <code className="rounded bg-slate-100 px-2 py-0.5 text-xs font-mono text-slate-600">
                                {formData.integrationRef}
                            </code>
                        </dd>
                    </div>
                    <div className="sm:col-span-2">
                        <dt className="text-xs font-medium uppercase tracking-wide text-slate-400">Credentials</dt>
                        <dd className="mt-0.5 flex flex-wrap gap-1.5">
                            {Object.keys(formData.credentials).filter(k => formData.credentials[k]).map((key) => (
                                <Badge key={key} variant="outline" className="text-[10px]">
                                    {key}: ••••••
                                </Badge>
                            ))}
                            {Object.keys(formData.credentials).filter(k => formData.credentials[k]).length === 0 && (
                                <span className="text-xs text-slate-400 italic">
                                    {formData.authType.startsWith('oauth2_authorization_code')
                                        ? 'Will be obtained via OAuth redirect'
                                        : 'None provided'}
                                </span>
                            )}
                        </dd>
                    </div>
                </dl>
            </div>
        </div>
    );
}
